/**
 * Signaling over BitTorrent WebSocket trackers, the fallback.
 *
 * A different set of machines run by different people on a different protocol, which is the whole
 * point of having a second entry: a network that blocks or breaks one is unlikely to break both.
 * It is second rather than first because the tracker list is shorter and the trackers themselves
 * rate-limit announces, so it is the less forgiving of the two under load.
 *
 * The package is separate from `trystero` itself, which now ships the Nostr strategy. That is what
 * keeps the fallback out of a session that never needs it.
 */

import { probeRelays } from '@/net/signaling/relaySocketProbe';
import type * as TrysteroTorrent from '@trystero-p2p/torrent';
import type { SignalingStrategy } from '@/net/signaling/signalingContract';

const load = (): Promise<typeof TrysteroTorrent> => import('@trystero-p2p/torrent');

const relays = async (): Promise<readonly string[]> => (await load()).defaultRelayUrls;

export const BITTORRENT_SIGNALING = {
  id: 'bittorrent',
  label: 'BitTorrent trackers',
  relays,
  selfId: async (): Promise<string> => (await load()).selfId,
  isReachable: async (timeoutMs, signal): Promise<boolean> =>
    probeRelays(await relays(), timeoutMs, signal),
  join: async (config, roomId, callbacks) => (await load()).joinRoom(config, roomId, callbacks),
} as const satisfies SignalingStrategy;
